import React from "react";

export type MessageType = "error" | "warning" | "info" | "success";
export type MessageScope = "page" | "app";

interface MessageOptions {
  closable?: boolean;
  scope?: MessageScope;
  linkTo?: string;
}

export interface Message {
  id: string;
  message: React.ReactNode | string;
  type: MessageType;
  options: MessageOptions;
}

interface MessageContextValue {
  messages: Message[];
  showErrorMessage: (
    message: React.ReactNode | string,
    options?: MessageOptions
  ) => string | undefined;
  showInfoMessage: (
    message: React.ReactNode | string,
    options?: MessageOptions
  ) => string | undefined;
  showWarningMessage: (
    message: React.ReactNode | string,
    options?: MessageOptions
  ) => string | undefined;
  showSuccessMessage: (
    message: React.ReactNode | string,
    options?: MessageOptions
  ) => string | undefined;
  closeMessage: (id: string) => void;
  closeAllPageScopeMessages: () => void;
  closeAllMessages: () => void;
  closeAllWarnings: () => void;
}

interface MessagesProviderProps {
  children?: React.ReactNode;
}

let curId = 0;

const MessagesContext = React.createContext<MessageContextValue | undefined>(
  undefined
);

export function useMessages() {
  const contextValue = React.useContext(MessagesContext);

  if (!contextValue) {
    throw new Error("The useMessage needs to be used inside a MessageProvider");
  }

  return contextValue;
}

function MessagesProvider(props: MessagesProviderProps) {
  const { children } = props;

  const [messages, setMessages] = React.useState<Message[]>([]);

  const showMessage = React.useCallback(
    (
      type: MessageType,
      message: React.ReactNode | string,
      options?: MessageOptions
    ) => {
      return new Promise<string | undefined>((resolve) => {
        if (!message) {
          resolve(undefined);
          return;
        }

        setMessages((prev) => {
          const msg = prev.find((item) => item.message === message);
          if (msg) {
            resolve(msg.id);
            return prev;
          }
          curId++;
          const messageId = String(curId);
          resolve(messageId);
          return [
            ...prev,
            {
              id: messageId,
              message,
              type,
              options: {
                closable: true,
                scope: "page",
                ...options,
              },
            },
          ];
        });
      });
    },
    []
  );

  const showErrorMessage = React.useCallback(
    async (message: React.ReactNode | string, options?: MessageOptions) => {
      return await showMessage("error", message, options);
    },
    [showMessage]
  );

  const showInfoMessage = React.useCallback(
    async (message: React.ReactNode | string, options?: MessageOptions) => {
      return await showMessage("info", message, options);
    },
    [showMessage]
  );

  const showWarningMessage = React.useCallback(
    async (message: React.ReactNode | string, options?: MessageOptions) => {
      return await showMessage("warning", message, options);
    },
    [showMessage]
  );

  const showSuccessMessage = React.useCallback(
    async (message: React.ReactNode | string, options?: MessageOptions) => {
      return await showMessage("success", message, options);
    },
    [showMessage]
  );

  const closeMessage = React.useCallback((id: string) => {
    setMessages((prev) => prev.filter((message) => message.id !== id));
  }, []);

  const closeAllPageScopeMessages = React.useCallback(() => {
    setMessages((prev) => {
      return prev.filter((message) => {
        return message.options.scope !== "page";
      });
    });
  }, []);

  const closeAllMessages = React.useCallback(() => {
    setMessages([]);
  }, []);

  const closeAllWarnings = React.useCallback(() => {
    setMessages((prev) => {
      return prev.filter((message) => {
        return message.type !== "warning";
      });
    });
  }, []);

  const messagesOrdered = React.useMemo(() => {
    return [
      ...messages.filter((message) => message.options.closable === false),
      ...messages.filter((message) => message.options.closable === true),
    ];
  }, [messages]);

  const contextValue = React.useMemo(() => {
    return {
      closeAllMessages,
      closeAllPageScopeMessages,
      closeMessage,
      closeAllWarnings,
      messages: messagesOrdered,
      showErrorMessage,
      showInfoMessage,
      showSuccessMessage,
      showWarningMessage,
    };
  }, [
    closeAllMessages,
    closeAllPageScopeMessages,
    closeMessage,
    closeAllWarnings,
    messagesOrdered,
    showErrorMessage,
    showInfoMessage,
    showSuccessMessage,
    showWarningMessage,
  ]);

  return (
    <MessagesContext.Provider
      value={contextValue as unknown as MessageContextValue}
    >
      {children}
    </MessagesContext.Provider>
  );
}

export default MessagesProvider;
